/**
 * The Genre Mixer's twelve-cell Music DNA preview grid.
 *
 * SIMULATED — every value is a hash of the mixer settings, not an analysis. See
 * buildMusicDna() in src/lib/music.ts.
 *
 * Extracted from the route purely for size: the grid is a self-contained block of
 * markup with one input, so it reads better here than as 20 lines in the middle of
 * a ten-section page. The `lyx-grid-2` class and cell styles are verbatim.
 */
import { c } from '../../styles/theme';
import type { buildMusicDna } from '../../lib/music';

/** The analysed profile, as returned by buildMusicDna(). Never null here. */
export type MixerDna = NonNullable<ReturnType<typeof buildMusicDna>>;

/** One cell in the grid. */
function DnaCell({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        background: c.surface1,
      }}
    >
      {/* The only thing that says what the number under it means, at 10px and
          uppercase — the worst case for a faded colour. text2. */}
      <p
        style={{
          fontSize: 10,
          color: c.text2,
          margin: '0 0 3px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{value}</p>
    </div>
  );
}

export interface MixerDnaPreviewProps {
  dna: MixerDna;
}

export default function MixerDnaPreview({ dna }: MixerDnaPreviewProps) {
  return (
    <div style={{ marginBottom: 18 }}>
      <p style={{ fontSize: 12, color: c.text2, margin: '16px 0 8px' }}>Music DNA preview</p>
      <div className="lyx-grid-2">
        <DnaCell label="Primary genre" value={dna.primaryGenre} />
        <DnaCell label="Secondary genre" value={dna.secondaryGenre} />
        <DnaCell label="Musical energy" value={`${dna.energy}%`} />
        <DnaCell label="Instrumentation" value={dna.instrumentation} />
        <DnaCell label="Production style" value={dna.production} />
        <DnaCell label="Song structure" value={dna.structure} />
        <DnaCell label="Atmosphere" value={dna.atmosphere} />
        <DnaCell label="Danceability" value={`${dna.danceability}%`} />
        <DnaCell label="Emotional tone" value={dna.emotionalTone} />
        <DnaCell label="Target audience" value={dna.audience} />
        <DnaCell label="Commercial appeal" value={`${dna.commercial}%`} />
        <DnaCell label="Tempo" value={`${dna.bpm} BPM`} />
      </div>
    </div>
  );
}
